/**
 * Plot Matrix (JSON) dev-tool — matrix builder.
 *
 * Runs the exact same calculation engine the live embedded `PlotMatrix.tsx`
 * uses (`generatePlotMatrixCategoryColumns`/`Rows`, the per-section BoQ
 * reconciliation targets, the Dynamic Bill Summary → Plot Matrix overlay) so
 * the numbers this dev-mode tab shows match the production Plot Matrix cell
 * for cell — the only thing that differs is where `groundworksData` comes
 * from (see `plotMatrixApiSnapshot.ts`: a `boq-versions` API snapshot, not
 * live `state.groundworks`).
 *
 * `buildPlotMatrixJson` is the explicit "step 1" of this feature: it returns
 * one plain, JSON-serializable object. `PlotMatrixJsonTable.tsx` (step 2)
 * renders purely from that object's `columns`/`rows`/`columnTotals` — it
 * never touches `groundworksData`, an item library, or any BoQ API itself.
 */
import { ItemLibraryResponse } from '../api/itemLibrary';
import { RetainingWallsBuildUpSpecification } from '../api/retainingWallsBuildUp';
import { GeoToberWallBuildUpSpecification } from '../api/wallSectionBuildUp';
import {
  generatePlotMatrixCategoryColumns,
  generatePlotMatrixCategoryRows,
  computeSoilBoQTotals,
  computeCutFillFieldBillSummaryData,
  DEFAULT_CELL_CALC_STRATEGY,
} from './plotMatrixCategoryGenerator';
import type {
  CellCalcStrategy,
  BoqStepTargets,
  PlotMatrixCategoryColumn,
  PlotMatrixRow,
} from './plotMatrixCategoryGenerator';
import { applyDynamicBillSummaryToPlotMatrixRows } from './dynamicBillSummaryPlotMatrix';
import {
  calculateCutFillGrandTotal,
  calculateFoundationsGrandTotal,
  calculateDrainageGrandTotal,
  calculatePlotDrivesGrandTotal,
  calculatePlotServiceGrandTotal,
  calculatePlotPavingsGrandTotal,
  calculateScaffoldGrandTotal,
  calculateGardenGrandTotal,
  calculateRetScreenWallsGrandTotal,
  calculateGEOToberWallGrandTotal,
} from './summaryCalculations';

export interface PlotMatrixJsonResult {
  columns: PlotMatrixCategoryColumn[];
  rows: PlotMatrixRow[];
  columnTotals: Record<string, number>;
  grandTotal: number;
  generatedAt: string;
}

export interface BuildPlotMatrixJsonOptions {
  groundworksData: Record<string, unknown>;
  itemLibraryData?: ItemLibraryResponse;
  ohpPercent: number;
  preliminariesPercent: number;
  includeOhp: boolean;
  includePreliminaries: boolean;
  retainingWallsBuildUpSpecs?: RetainingWallsBuildUpSpecification[];
  geoToberWallBuildUpSpecs?: GeoToberWallBuildUpSpecification[];
  cellCalcStrategy?: CellCalcStrategy;
}

export function buildPlotMatrixJson(options: BuildPlotMatrixJsonOptions): PlotMatrixJsonResult {
  const {
    groundworksData,
    itemLibraryData,
    ohpPercent,
    preliminariesPercent,
    includeOhp,
    includePreliminaries,
    retainingWallsBuildUpSpecs,
    geoToberWallBuildUpSpecs,
    cellCalcStrategy = DEFAULT_CELL_CALC_STRATEGY,
  } = options;

  const gwBoq = groundworksData as any;
  const columns = generatePlotMatrixCategoryColumns(gwBoq);
  const soilBoQTotals = computeSoilBoQTotals(gwBoq, itemLibraryData);

  // Same sequential OH&P-then-Prelims formula every fixed Bill Summary and
  // Sum Analysis already apply — see PlotMatrix.tsx's own `upliftFactor`.
  const upliftFactor =
    (1 + (includeOhp ? ohpPercent : 0) / 100) * (1 + (includePreliminaries ? preliminariesPercent : 0) / 100);

  // `fillToMakeUp6F2` is counted in Cut & Fills' BoQ total but displayed
  // under Plot Drives in the matrix — shift it between the two section
  // targets so the reconciliation below doesn't misattribute the delta.
  const cutFillFieldData = itemLibraryData
    ? computeCutFillFieldBillSummaryData(gwBoq, itemLibraryData)
    : new Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }>();
  const fill6F2Amount = cutFillFieldData.get('fillToMakeUp6F2')?.billSummaryAmount ?? 0;
  const fill6F2MarkedUp = fill6F2Amount * upliftFactor;

  const boqStepTargets: BoqStepTargets | undefined = itemLibraryData
    ? new Map<string, number>([
        ['Cut & Fills', calculateCutFillGrandTotal(gwBoq, null, itemLibraryData, upliftFactor) - fill6F2MarkedUp],
        ['Foundations', calculateFoundationsGrandTotal(gwBoq, null, itemLibraryData, upliftFactor)],
        ['Drainage', calculateDrainageGrandTotal(gwBoq, null, itemLibraryData, upliftFactor)],
        ['Plot Drives', calculatePlotDrivesGrandTotal(gwBoq, null, itemLibraryData, upliftFactor) + fill6F2MarkedUp],
        ['Plot Service', calculatePlotServiceGrandTotal(gwBoq, null, itemLibraryData, upliftFactor)],
        ['Plot Pavings', calculatePlotPavingsGrandTotal(gwBoq, null, itemLibraryData, upliftFactor)],
        ['Scaffold', calculateScaffoldGrandTotal(gwBoq, null, itemLibraryData, upliftFactor)],
        ['Garden', calculateGardenGrandTotal(gwBoq, null, itemLibraryData, upliftFactor)],
        [
          'Ret & Screen Walls',
          calculateRetScreenWallsGrandTotal(gwBoq, null, itemLibraryData, retainingWallsBuildUpSpecs, upliftFactor) +
            calculateGEOToberWallGrandTotal(gwBoq, null, itemLibraryData, geoToberWallBuildUpSpecs, upliftFactor),
        ],
      ])
    : undefined;

  const baseRows = generatePlotMatrixCategoryRows(
    gwBoq,
    columns,
    null,
    itemLibraryData,
    soilBoQTotals,
    ohpPercent,
    cellCalcStrategy,
    upliftFactor,
    undefined,
    boqStepTargets,
  );

  const rows = applyDynamicBillSummaryToPlotMatrixRows(
    baseRows,
    columns,
    gwBoq.dynamicBillSummaryTabs ?? [],
    upliftFactor,
    gwBoq.cutFillsExtraItems,
    gwBoq.foundationsExtraItems,
    gwBoq.drainageExtraItems,
    gwBoq.plotDrivesExtraItems,
    gwBoq.plotServiceExtraItems,
    gwBoq.scaffoldExtraItems,
    gwBoq.plotPavingsExtraItems,
    gwBoq.gardenExtraItems,
    gwBoq.retainingWallsExtraItems,
    gwBoq.geoWallsExtraItems,
  );

  const columnTotals: Record<string, number> = {};
  columns.forEach((col) => {
    columnTotals[col.dataIndex] = rows.reduce(
      (sum, row) => sum + ((row as any)[col.dataIndex]?.total || 0),
      0,
    );
  });
  const grandTotal = Object.values(columnTotals).reduce((sum, value) => sum + value, 0);

  return {
    columns,
    rows,
    columnTotals,
    grandTotal,
    generatedAt: new Date().toISOString(),
  };
}
